/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import {
  Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges,
} from '@angular/core';
import { MenuItem } from 'primeng/api';
import { generateObjectId } from '../../../app/exports';
import { UnitSimpleDTO } from '../unit-simple-dto.model';
import { TranslationService } from '../../../common/translation.service';

/**
 * Angular component that render a breadcrumb trail from an array of units.
 */
@Component({
  selector: 'bulb-resume-content-selection-breadcrumbs',
  templateUrl: './content-selection-breadcrumbs.component.html',
  standalone: false,
})
export class ResumeContentSelectionBreadCrumbsComponent implements OnInit, OnChanges {
  /**
   * Event emitter fired when the user selects a bread crumb in the trail.
   */
  @Output() onBreadCrumbClick : EventEmitter< any > = new EventEmitter< any >();

  /**
   * Passed in Collection to add to the breadcrumbs list.
   */
  @Input() selectedCollection : UnitSimpleDTO;

  /**
   * Reference to the original units that were displayed.
   */
  @Input() units : Array< UnitSimpleDTO > = new Array< UnitSimpleDTO >();

  /**
   * Array of PrimeNG Menu items for creating the breadcrumbs.
   */
  public breadCrumbs : MenuItem[];

  /**
   * Reference to the original units that were displayed.
   */
  private originalUnits : Array< UnitSimpleDTO > = new Array< UnitSimpleDTO >();

  /**
   * Default Constructor.
   *
   * @param translationService
   */
  constructor(private translationService : TranslationService) {}

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a
   * directive or component. Used to handle any additional initialization tasks.
   */
  ngOnInit() {
    this.initializeBreadCrumbs();
  }

  /**
   * A lifecycle hook that is called when any data-bound property of a component changes.
   *
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    // Keep reference to the original units that were displayed in the content selection display component:
    if (changes.units.previousValue) {
      if (changes.units.previousValue.length === 0) {
        this.originalUnits = changes.units.currentValue;
      }
    }
    if (changes.selectedCollection) {
      const collection : UnitSimpleDTO = changes.selectedCollection.currentValue;
      if (collection) {
        const menuItem : MenuItem = {
          id: this.formatMenuItemId(collection),
          label: collection.name,
          styleClass: 'bulb-resume-content-selection-breadcrumb-currently-active',
          command: this.createMenuItemCommand.bind(this, changes),
        };
        this.breadCrumbs.push(menuItem);
        if (this.breadCrumbs.length > 2) {
          this.showBreadCrumbTrail();
        }
        /**
         * This is needed due to how / when Angular detection cycle runs and is triggered.
         *
         * Angular change detection only checks object identity, not object content.
         * Therefore, inserts or removals are not detected.
         */
        this.breadCrumbs = this.breadCrumbs.slice();
      }
    }
  }

  /**
   * Method that initializes the breadcrumbs.
   */
  private initializeBreadCrumbs() : void {
    this.breadCrumbs = [
      {
        label: this.translationService.translateInstant('all_content_breadcrumb'),
        id: 'all-content',
        styleClass: 'bulb-resume-content-selection-breadcrumb-redirect',
        command: this.createAllContentMenuItemCommand.bind(this),
      },
    ];
  }

  /**
   * Method that formats the collection name, so it could be queried from and is acceptable in the DOM.
   *
   * @param collection
   * @private
   */
  private formatMenuItemId(collection : UnitSimpleDTO) : string {
    return `bulb-csb-${collection.name.replace(/ /g, '-')
      .replace(/[^\w\s]/gi, '').toLowerCase()}-${generateObjectId()}`;
  }

  /**
   * Method that creates a menu item command that is fired when the menu item for showing all content is clicked.
   *
   * @private
   */
  private createAllContentMenuItemCommand() : void {
    // Clicking this menu item option, we want to show the 'original' units that were rendered in the
    // content-selection-display component:
    this.onBreadCrumbClick.emit(
      {
        showAllOriginalContent: true,
        originalUnits: this.originalUnits,
      },
    );
    // Destroy the built up breadcrumb trail :
    this.breadCrumbs.length = 1;
  }

  /**
   * Method that creates a menu item command that is fired when the menu item is clicked.
   *
   * @param changes
   * @param event
   * @private
   */
  private createMenuItemCommand(changes : SimpleChanges, event :any) : void {
    const activeBreadCrumbClass : string = 'bulb-resume-content-selection-breadcrumb-currently-active';
    const redirectBreadCrumbClass : string = 'bulb-resume-content-selection-breadcrumb-redirect';
    this.onBreadCrumbClick.emit({ units: changes.units.currentValue });
    // Create a jQuery object from event element itself, then iterate down or up through the DOM to obtain other
    // elements and apply / remove classes accordingly
    jQuery(event.originalEvent.target).parent().removeClass(redirectBreadCrumbClass)
      .parent()
      .addClass(activeBreadCrumbClass);
    // Update length of the breadcrumbs array:
    const index = this.breadCrumbs.map((item : MenuItem) => item.label).indexOf(event.item.label);
    this.breadCrumbs.length = (index + 1);
  }

  /**
   * Method that shows the breadcrumb trail when necessary.
   *
   * @private
   */
  private showBreadCrumbTrail() : void {
    // Timeout is needed, since the new element hasn't been created and displayed in the DOM yet on the ngOnChanges
    // event.
    setTimeout(() => {
      const activeBreadCrumbClass : string = 'bulb-resume-content-selection-breadcrumb-currently-active';
      const redirectBreadCrumbClass : string = 'bulb-resume-content-selection-breadcrumb-redirect';
      const breadCrumb : any = this.breadCrumbs[this.breadCrumbs.length - 2];
      // Start from the the container that houses all the menu items, and find specific menu item from there:
      jQuery('p-breadcrumb').find(`#${breadCrumb.id}`)
        .addClass(redirectBreadCrumbClass).parent()
        .removeClass(activeBreadCrumbClass);
    });
  }
}
